/**
 * Error handling utilities
 */

/** The backend detail as it arrives: httpClient rewrites both of its failure paths to
 * `{ detail }`, and an unwrapped FastAPI error carries the same key. A wrapped envelope
 * may carry a list, so readers decide for themselves whether to unwrap one. */
const responseDetail = (error: unknown): string | string[] | undefined =>
  (error as { response?: { data?: { detail?: string | string[] } } })?.response?.data?.detail;

/** Backend HTTPException detail if present, else the fallback. A list is not a detail
 * this reader renders: its callers wrote the fallback copy for that case. */
export const responseStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

/** A response header of a failed request (lowercase name), if it carried one. */
export const responseHeader = (error: unknown, name: string): string | undefined =>
  (error as { response?: { headers?: Record<string, string | undefined> } })?.response?.headers?.[name];

export interface CreditShortfall {
  available: number;
  required: number;
}

/** The amounts a 402 refusal names when the workspace cannot cover an approval, else null. */
export const creditShortfall = (error: unknown): CreditShortfall | null => {
  if (responseStatus(error) !== 402) return null;
  const data = (error as { response?: { data?: { data?: Partial<CreditShortfall> } } }).response?.data?.data;
  return typeof data?.available === 'number' && typeof data.required === 'number'
    ? { available: data.available, required: data.required }
    : null;
};

export function getErrorDetail(error: unknown, fallback: string): string {
  const detail = responseDetail(error);
  return typeof detail === 'string' && detail ? detail : fallback;
}

/** The backend detail without pydantic's "Value error, " prefix, or null to leave the
 * wording to the caller. Not status-filtered: a 5xx detail reaches the caller too. */
export const serverDetail = (error: unknown): string | null => {
  const detail = responseDetail(error);
  const first = Array.isArray(detail) ? detail[0] : detail;
  return typeof first === 'string' && first ? first.replace(/^Value error, /, '') : null;
};

export interface ErrorResult {
  message: string;
  details?: string;
  requiresUpgrade: boolean;
  isDuplicateColumn?: boolean;
  shouldReturnToIntro?: boolean;
}

/**
 * Parse any error into a standardized result for UI display
 */
export const parseError = (error: any): ErrorResult => {
  const rawDetail = error?.response?.data?.detail;
  const status = error?.response?.status;

  if (typeof rawDetail === 'object' && rawDetail !== null) {
    if (rawDetail.error === 'free_tier_limit_exceeded') {
      return {
        message: rawDetail.message || 'Free plan limit reached. Please select fewer results.',
        requiresUpgrade: false,
        shouldReturnToIntro: true,
      };
    }
    if (rawDetail.message) {
      const errorType = typeof rawDetail.error === 'string' ? rawDetail.error : '';
      return {
        message: rawDetail.message,
        requiresUpgrade: errorType.includes('credit') || errorType.includes('upgrade'),
      };
    }
  }

  if (typeof rawDetail === 'string') {
    if (rawDetail.includes('free_tier_limit_exceeded')) {
      const messageMatch = rawDetail.match(/'message':\s*'([^']+)'/);
      return {
        message: messageMatch ? messageMatch[1] : 'Free plan limit reached. Please select fewer results.',
        requiresUpgrade: false,
        shouldReturnToIntro: true,
      };
    }
  }

  const rawError = typeof rawDetail === 'string' ? rawDetail : (error?.response?.data?.error || '');
  const responseError = typeof rawError === 'string' ? rawError : '';
  const messageError = typeof error?.message === 'string' ? error.message : '';
  const errorStr = (responseError || messageError).toLowerCase();

  // Subscription/upgrade required
  if (errorStr.includes('credit') || errorStr.includes('upgrade') || errorStr.includes('run out') || errorStr.includes('paid') || errorStr.includes('subscription')) {
    return {
      message: responseError || messageError || 'Please upgrade your plan to continue.',
      requiresUpgrade: true,
    };
  }

  if (errorStr.includes('free') && (errorStr.includes('tier') || errorStr.includes('plan') || errorStr.includes('limit'))) {
    return {
      message: 'Free plan limit reached. Please select fewer results.',
      requiresUpgrade: false,
      shouldReturnToIntro: true,
    };
  }

  // Rate limit
  if (status === 429) {
    return {
      message: 'Too many requests.',
      details: 'Please wait a moment before trying again.',
      requiresUpgrade: false,
    };
  }

  // Network error
  if (errorStr.includes('network') || errorStr.includes('fetch') || errorStr.includes('connect')) {
    return {
      message: 'Unable to connect to the server.',
      details: 'Please check your internet connection and try again.',
      requiresUpgrade: false,
    };
  }

  // Auth error
  if (status === 401 || errorStr.includes('unauthorized') || errorStr.includes('session')) {
    return {
      message: 'Your session has expired.',
      details: 'Please refresh the page and try again.',
      requiresUpgrade: false,
    };
  }

  // Duplicate column
  if (errorStr.includes('already exists') || errorStr.includes('duplicate')) {
    return {
      message: 'A column with this name already exists.',
      details: 'Please choose a different column name.',
      requiresUpgrade: false,
      isDuplicateColumn: true,
    };
  }

  // Server error
  if (status >= 500) {
    return {
      message: 'Server error occurred.',
      details: 'Please try again later.',
      requiresUpgrade: false,
    };
  }

  // Generic - use response message if available
  return {
    message: responseError || messageError || 'Something went wrong. Please try again.',
    requiresUpgrade: false,
  };
};

// Legacy export for backwards compatibility
export const determineErrorType = (error: any) => {
  const result = parseError(error);
  return {
    errorMessage: result.message,
    shouldShowOutOfCredits: result.requiresUpgrade,
  };
};